import { truncate } from 'lodash';
import React, {
  // useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  Suspense,
} from 'react';
import strip from 'strip';
import { Col, Row } from 'components/Grid';
import ProductCard from 'containers/cards/Product';
// import CommentForm from 'components/CommentForm';
import Page from 'containers/Page';
import PageSubHeading from 'containers/Page/SubHeading';
import { useRouter } from 'found';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAuthContext } from 'AuthContext';
import { useTheme } from '@mui/material';
import useIsMobile from 'lib/useIsMobile';
import ProductMeta from './Meta';
import { GET_FETCH_PRODUCTS_DETAILS_INFO } from '../../schema/apolloQueries/communityDetailsQueries';
import { LIKED_UNLIKED_SPONSOR } from '../../schema/apolloMutations/eventDeatilsMutations';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';
import { useAudienceTrackerContext } from '../../lib/trackers/Audience';

const Feed = React.lazy(() => import('feed/Feed'));

const styles = {
  card: { marginBottom: 20 },
};

const ProductPage = () => {
  const {
    match: {
      location: { pathname },
      params: { productSlug },
    },
  } = useRouter();

  const { trackView } = useAudienceTrackerContext();

  const { data } = useQuery(GET_FETCH_PRODUCTS_DETAILS_INFO, {
    variables: {
      slug: encodeURIComponent(productSlug),
    },
  });

  // const [creatingComment, setCreatingComment] = useState(false);

  const [handleLike] = useMutation(LIKED_UNLIKED_SPONSOR);

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const isMobile = useIsMobile();
  const { showSignInDialog } = useAuthContext();
  const theme = useTheme({ isMobile });
  const feedRef = useRef(null);
  const [feedMF, setFeedMF] = useState(null);

  const heroAssetUrl = Array.isArray(data?.fetchProductDetails?.asset)
    ? data?.fetchProductDetails?.asset[0]?.hlsPlaylistUrl
    : '';

  // const first = !data?.fetchProductDetails?.isReviewable
  //   && data?.fetchProductDetails?.commentCount === 0;

  const metaDescription = `${
    data?.fetchProductDetails?.summary
      ? strip(data?.fetchProductDetails?.summary)
      : truncate(strip(data?.fetchProductDetails?.description), {
          length: 140,
          separator: ' ',
        })
  } #sponsored`;

  const metaTitle = strip(data?.fetchProductDetails?.name);

  const productMeta = useMemo(
    () => <ProductMeta product={data?.fetchProductDetails} />,
    [data?.fetchProductDetails],
  );

  useEffect(() => {
    if (
      feedRef.current
      && !feedMF
      && data?.fetchProductDetails?.id
      && profileData
    ) {
      setFeedMF(
        <Suspense fallback='Loading Feed...'>
          <Feed
            context={{
              modelType: 'Product',
              productId: data?.fetchProductDetails?.id,
              personId: profileData?.getProfile?.personId,
              loggedIn: profileData?.getProfile?.loggedIn,
              email: profileData?.getProfile?.personInfo?.email,
              showSignInDialog,
              enabledFeedTypes: ['Review'],
              sponsorId: data?.fetchProductDetails?.sponsorId,
            }}
            themeType={theme.palette.mode}
          />
        </Suspense>,
      );
    }
  }, [
    data?.fetchProductDetails,
    showSignInDialog,
    profileData,
    feedMF,
    feedRef,
    theme.palette.mode,
  ]);

  useEffect(() => {
    if (data?.fetchProductDetails?.name) {
      trackView('product_details', {
        page_name: data?.fetchProductDetails?.name,
        page_url: pathname,
        product_slug: productSlug,
        product_id: data?.fetchProductDetails?.id,
      });
    }
  }, [productSlug, data?.fetchProductDetails?.name]);

  return (
    <Page
      backLink={useMemo(
        () => ({
          label: data?.fetchProductDetails?.sponsorName,
          href: `/communities/${data?.fetchProductDetails?.sponsorSlug}`,
        }),
        [data?.fetchProductDetails?.name, data?.fetchProductDetails?.slug],
      )}
      meta={useMemo(
        () => [
          {
            property: 'og:type',
            content: 'article',
          },
          {
            property: 'og:tag',
            content: 'sponsored',
          },
          {
            property: 'og:title',
            content: metaTitle,
          },
          {
            property: 'og:description',
            content: metaDescription,
          },
          heroAssetUrl
            ? {
                property: 'og:image',
                content: heroAssetUrl,
              }
            : {},
          {
            property: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            property: 'twitter:title',
            content: metaTitle,
          },
          {
            property: 'twitter:description',
            content: metaDescription,
          },
          heroAssetUrl
            ? {
                property: 'twitter:image',
                content: heroAssetUrl,
              }
            : {},
        ],
        [heroAssetUrl, metaTitle, metaDescription],
      )}
      rightDrawer={productMeta}
      title={data?.fetchProductDetails?.name}
      trackerTitle={`Products > ${data?.fetchProductDetails?.name}`}
      viewer={profileData?.getProfile}
    >
      <Row>
        <Col main>
          <ProductCard
            assetKey='asset'
            assetTypeKey='assetType'
            product={data?.fetchProductDetails}
            style={styles.card}
            viewer={profileData?.getProfile}
            handleLike={handleLike}
          />
          {data?.fetchProductDetails?.isReviewable && (
            <PageSubHeading>Reviews</PageSubHeading>
          )}
          {/* <PageSubHeading>
            {data?.fetchProductDetails?.isReviewable ? 'Reviews' : 'Comments'}
          </PageSubHeading> */}
          {/* {useMemo(
            () =>
              !data?.fetchProductDetails?.isReviewable && (
                <CommentForm
                  first={first}
                  onCreate={onCreate}
                  saving={creatingComment}
                />
              ),
            [
              creatingComment,
              first,
              onCreate,
              data?.fetchProductDetails?.isReviewable,
            ]
          )} */}
          <div
            ref={feedRef}
            style={{ width: '100%', minHeight: 300, margin: '0px 0 20px' }}
          >
            {feedMF}
          </div>
        </Col>
        <Col meta>{productMeta}</Col>
      </Row>
    </Page>
  );
};

export default ProductPage;
